import {
  FONO_AGE_OPTIONS,
  IDTEL_AGE_OPTIONS,
  WISC5_AGE_OPTIONS,
  WAIS4_AGE_OPTIONS,
  SENSORIAL_ADULTOS_AGE_OPTIONS,
  SPM2_AGE_OPTIONS,
} from "./ageOptions";
import { FONO_MARK_INCORRECTS_HINT } from "./fonoInstructions";
import { SENSORIAL_AGE_OPTIONS } from "../data/sensorialPadresData";
import { CARAS_R_BAREMO_OPTIONS } from "../data/carasData";

/** @typedef {'single' | 'teprosif' | 'idtel'} TestFlowType */

/**
 * Test correction catalog — mirrors ChatbotTestCorrection.TEST_CATEGORIES with
 * UI metadata for the landing component. TestEnum `value` drives API payloads.
 */
export const TEST_FLOW = {
  SINGLE: "single",
  TEPROSIF: "teprosif",
  IDTEL: "idtel",
};

/** IDTEL is corrected in N sequential RedPsi steps (indexes 0 … N-1). */
export const IDTEL_STEP_COUNT = 15;

export const TEST_PROFESSIONS = [
  {
    id: "fono",
    label: "Fonoaudiología",
    kicker: "Fonoaudiología",
  },
  {
    id: "psico",
    label: "Psicología",
    kicker: "Psicología",
  },
  {
    id: "to",
    label: "Terapia ocupacional",
    kicker: "Terapia ocupacional",
  },
  {
    // platformOnly: solo dentro del colegio (/redpie#/), nunca en el producto
    // gratis a granel. Los dos Evalúa no tienen template individual de informe
    // en granel, así que ofrecerlos ahí terminaría en una descarga fallida.
    // El gate va en la profesión y no en cada test porque la única forma de
    // llegar a ellos es teniéndola seleccionada: ver getProfessions y su uso
    // en TestToolbar (el <select>) y en CorregirTestWorkspace.
    id: "psicopedagogia",
    label: "Psicopedagogía",
    kicker: "Psicopedagogía",
    platformOnly: true,
  },
];

export const TESTS = [
  {
    slug: "teprosif",
    professionId: "fono",
    value: 3,
    label: "TEPROSIF-R",
    icon: "🔤",
    panelSub:
      "Palabra, emisión y omitida · procesos ocultos hasta corregir",
    panelHint: FONO_MARK_INCORRECTS_HINT,
    requiresAge: true,
    ageOptions: FONO_AGE_OPTIONS,
    flow: TEST_FLOW.TEPROSIF,
    correctActionLabel: "Corregir emisiones",
    reportTitle: "Informe TEPROSIF-R listo para Word",
  },
  {
    slug: "stsg",
    professionId: "fono",
    value: 5,
    label: "STSG",
    icon: "🗣️",
    panelSub: "Comprensión receptiva y expresiva · 23 ítems por sección",
    panelHint: FONO_MARK_INCORRECTS_HINT,
    requiresAge: true,
    ageOptions: FONO_AGE_OPTIONS,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe STSG listo para Word",
  },
  {
    slug: "idtel",
    professionId: "fono",
    value: 7,
    label: "IDTEL",
    icon: "🧩",
    panelSub: "Microdominios fonológico, morfosintáctico, semántico y pragmático",
    panelHint: FONO_MARK_INCORRECTS_HINT,
    requiresAge: true,
    ageOptions: IDTEL_AGE_OPTIONS,
    flow: TEST_FLOW.IDTEL,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe IDTEL listo para Word",
  },
  {
    slug: "tecal",
    professionId: "fono",
    value: 9,
    label: "TECAL",
    icon: "🔢",
    panelSub: "101 ítems · selección de alternativa por pregunta",
    panelHint: FONO_MARK_INCORRECTS_HINT,
    requiresAge: true,
    ageOptions: FONO_AGE_OPTIONS,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe TECAL listo para Word",
  },
  {
    slug: "wais4",
    professionId: "psico",
    value: 0,
    label: "WAIS-IV",
    icon: "🧠",
    panelSub: "Puntajes brutos por subprueba · adultos",
    panelHint: "Ingresa los puntajes brutos de cada subprueba del protocolo.",
    requiresAge: true,
    ageOptions: WAIS4_AGE_OPTIONS,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe WAIS-IV listo para Word",
  },
  {
    slug: "icap",
    professionId: "psico",
    value: 15,
    label: "ICAP",
    icon: "📊",
    panelSub: "Escala chilena · conducta adaptativa e intelectual",
    panelHint:
      "Registra los puntajes de cada ítem según la escala del protocolo ICAP.",
    requiresAge: false,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe ICAP listo para Word",
  },
  {
    slug: "wisc5",
    professionId: "psico",
    value: 11,
    label: "WISC-V",
    icon: "🧠",
    panelSub: "Puntajes brutos por subprueba · escolar",
    panelHint: "Ingresa los puntajes brutos de cada subprueba del protocolo.",
    requiresAge: true,
    ageOptions: WISC5_AGE_OPTIONS,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe WISC-V listo para Word",
  },
  {
    slug: "caras-r",
    professionId: "psico",
    value: 23,
    label: "CARAS-R",
    icon: "🙂",
    panelSub: "Percepción de diferencias · atención · 6 a 18 años",
    panelHint:
      "Ingresa el total de aciertos y de errores de la hoja de respuestas.",
    requiresAge: true,
    ageOptions: CARAS_R_BAREMO_OPTIONS,
    ageLabel: "Curso y baremo",
    agePlaceholder: "Selecciona curso y baremo",
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe CARAS-R listo para Word",
  },
  {
    slug: "conners",
    professionId: "psico",
    value: 24,
    label: "Conners · Apoderados",
    icon: "📝",
    panelSub: "Escala abreviada ampliada por ejes · la responde el apoderado",
    panelHint:
      "Marca cuánto ocurre cada conducta en los últimos seis meses.",
    requiresAge: false,
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe Conners listo para Word",
  },
  {
    slug: "sensorial-padres",
    professionId: "to",
    value: 16,
    label: "Perfil sensorial · Padres",
    icon: "🌈",
    panelSub: "Cuestionario para padres y apoderados",
    panelHint:
      "Indica la frecuencia con la que ocurre cada conducta descrita en el ítem.",
    requiresAge: true,
    ageOptions: SENSORIAL_AGE_OPTIONS,
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe Perfil Sensorial listo para Word",
  },
  {
    slug: "sensorial-escolar",
    professionId: "to",
    value: 17,
    label: "Perfil sensorial · Escolar",
    icon: "🏫",
    panelSub: "Cuestionario para el contexto escolar",
    panelHint:
      "Indica la frecuencia con la que ocurre cada conducta descrita en el ítem.",
    requiresAge: true,
    ageOptions: SENSORIAL_AGE_OPTIONS,
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe Perfil Sensorial escolar listo para Word",
  },
  {
    slug: "sensorial-adultos",
    professionId: "to",
    value: 18,
    label: "Perfil sensorial · Adolescentes y adultos",
    icon: "🧑",
    panelSub: "Autocuestionario · desde los 11 años",
    panelHint:
      "Indica la frecuencia con la que la persona realiza cada conducta descrita en el ítem.",
    requiresAge: true,
    ageOptions: SENSORIAL_ADULTOS_AGE_OPTIONS,
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe Perfil Sensorial adolescentes y adultos listo para Word",
  },
  {
    slug: "spm2-preescolar",
    professionId: "to",
    value: 19,
    label: "SPM-2 · Preescolar hogar",
    icon: "🧭",
    panelSub: "Cuestionario para padres o cuidadores · 2 a 5 años",
    panelHint:
      "Indica con qué frecuencia ocurre cada conducta durante el último mes.",
    requiresAge: true,
    ageOptions: SPM2_AGE_OPTIONS.preescolar_hogar,
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe SPM-2 listo para Word",
  },
  {
    slug: "spm2-nino",
    professionId: "to",
    value: 20,
    label: "SPM-2 · Niño hogar",
    icon: "🧭",
    panelSub: "Cuestionario para padres o cuidadores · 5 a 12 años",
    panelHint:
      "Indica con qué frecuencia ocurre cada conducta durante el último mes.",
    requiresAge: true,
    ageOptions: SPM2_AGE_OPTIONS.nino_hogar,
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe SPM-2 listo para Word",
  },
  {
    slug: "spm2-adolescente",
    professionId: "to",
    value: 21,
    label: "SPM-2 · Adolescente hogar",
    icon: "🧭",
    panelSub: "Cuestionario para padres o cuidadores · 12 a 21 años",
    panelHint:
      "Indica con qué frecuencia ocurre cada conducta durante el último mes.",
    requiresAge: true,
    ageOptions: SPM2_AGE_OPTIONS.adolescente_hogar,
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe SPM-2 listo para Word",
  },
  {
    slug: "spm2-autorreporte",
    professionId: "to",
    value: 22,
    label: "SPM-2 · Adolescente autorreporte",
    icon: "🧭",
    panelSub: "Lo responde el propio adolescente · 12 a 21 años",
    panelHint:
      "Indica con qué frecuencia ocurre cada conducta durante el último mes.",
    requiresAge: true,
    ageOptions: SPM2_AGE_OPTIONS.adolescente_autorreporte,
    standalone: true,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe SPM-2 listo para Word",
  },
  // Los dos Evalúa no tienen formulario propio en esta vista: TestFormRouter no
  // los encuentra en CUSTOM_FORMS y caen en LegacyTestForm → DynamicTestForm →
  // Evalua.vue / Evalua4.vue, los mismos componentes de form-test/ que usaba la
  // interfaz antigua. Traen su propio selector de nivel (0-10) y no piden edad,
  // por eso requiresAge: false.
  // Se distinguen por estandarización, como ya hacía el mapa testsNames de
  // index.vue: son tests distintos (TestEnum 2 y 12), no versiones del mismo.
  {
    slug: "evalua",
    professionId: "psicopedagogia",
    value: 2,
    label: "Evalúa (Estandarización 3)",
    icon: "📚",
    panelSub: "Batería psicopedagógica · elige el nivel del protocolo",
    panelHint:
      "Selecciona el nivel de Evalúa que aplicaste y registra las respuestas del protocolo.",
    requiresAge: false,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe Evalúa listo para Word",
  },
  {
    slug: "evalua4",
    professionId: "psicopedagogia",
    value: 12,
    label: "Evalúa (Estandarización 4)",
    icon: "📗",
    panelSub: "Batería psicopedagógica · elige el nivel del protocolo",
    panelHint:
      "Selecciona el nivel de Evalúa que aplicaste y registra las respuestas del protocolo.",
    requiresAge: false,
    flow: TEST_FLOW.SINGLE,
    correctActionLabel: "Corregir test",
    reportTitle: "Informe Evalúa listo para Word",
  },
];

const testsBySlug = Object.fromEntries(TESTS.map((t) => [t.slug, t]));
const testsByValue = Object.fromEntries(TESTS.map((t) => [t.value, t]));

/**
 * Tests that cannot coexist in the same session, declared by slug so the
 * TestEnum values stay sourced only from the catalog above.
 * (WAIS/WISC: adult vs. school Wechsler; sensorial: parents vs. school form.)
 *
 * Además, a granel los tests con `standalone: true` (toda T.O., CARAS-R y
 * Conners) se informan solos: no hay informe integrado para ellos, así que no
 * se combinan con ningún otro test de su profesión. Solo se integran WISC-V +
 * ICAP, WAIS-IV + ICAP y los de fono entre sí. Dentro del colegio esa regla no
 * rige (ver createTestCorrectionSession).
 */
export const MUTUALLY_EXCLUSIVE_GROUPS = [
  ["wais4", "wisc5"],
  ["sensorial-padres", "sensorial-escolar"],
].map((group) => group.map((slug) => testsBySlug[slug].value));

export function getProfessionById(id) {
  return TEST_PROFESSIONS.find((p) => p.id === id) || null;
}

/**
 * Profesiones ofrecibles. `platform` = estamos dentro del colegio (el
 * workspace lo pasa desde su prop requireStudent); en el producto gratis a
 * granel deja fuera las profesiones platformOnly.
 */
export function getProfessions(platform = false) {
  return TEST_PROFESSIONS.filter((p) => platform || !p.platformOnly);
}

/** ¿Esta profesión se puede ofrecer en el contexto dado? */
export function isProfessionAvailable(professionId, platform = false) {
  const profession = getProfessionById(professionId);
  return Boolean(profession) && (platform || !profession.platformOnly);
}

export function getTestsForProfession(professionId) {
  return TESTS.filter((t) => t.professionId === professionId);
}

export function getTestBySlug(slug) {
  return testsBySlug[slug] || null;
}

export function getTestByValue(value) {
  return testsByValue[value] || null;
}

export function getFlowType(testValue) {
  const test = getTestByValue(testValue);
  return test ? test.flow : TEST_FLOW.SINGLE;
}

export function getMutuallyExclusiveGroup(testValue) {
  return (
    MUTUALLY_EXCLUSIVE_GROUPS.find((group) => group.includes(testValue)) || null
  );
}

export function isTestBlocked(testValue, correctedTests, { granel = true } = {}) {
  if (granel) {
    const test = getTestByValue(testValue);
    const others = correctedTests
      .filter((entry) => entry.test !== testValue)
      .map((entry) => getTestByValue(entry.test))
      .filter((other) => other && test && other.professionId === test.professionId);
    if (others.length && (test.standalone || others.some((other) => other.standalone))) {
      return true;
    }
  }
  const group = getMutuallyExclusiveGroup(testValue);
  if (!group) return false;
  return correctedTests.some(
    (entry) => group.includes(entry.test) && entry.test !== testValue
  );
}

export function defaultSelection() {
  // getProfessions() y no TEST_PROFESSIONS[0]: el default tiene que ser válido
  // también en el producto a granel.
  const profession = getProfessions()[0];
  const tests = getTestsForProfession(profession.id);
  return {
    professionId: profession.id,
    testSlug: tests[0] ? tests[0].slug : null,
  };
}
